import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const InnerPageLayout = ({ 
  children,
  heroContent,
  customImage,
  layout, // Accept layout prop from page data
  imageAspect = 'landscape' // Accept imageAspect prop: 'square', 'landscape', 'portrait'
}) => {
  const [templateEnabled, setTemplateEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Placeholder image for the layout
  const placeholderImage = 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=800&auto=format&fit=crop&q=60';

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        // If layout is explicitly set to image_right or image_left, enable template mode
        if (layout === 'image_right' || layout === 'image_left') {
          setTemplateEnabled(true);
          setLoading(false);
          return;
        }
        
        const response = await axios.get(`${API_BASE_URL}/api/inner-page-template`, {
          params: { page_path: location.pathname }
        });
        // Template is enabled if we get a template back from the API
        if (response.data?.template) {
          setTemplateEnabled(true);
        } else {
          setTemplateEnabled(false);
        }
      } catch (error) {
        console.error('Error fetching inner page template:', error);
        // If layout is image_right or image_left, still enable even if API fails
        if (layout === 'image_right' || layout === 'image_left') {
          setTemplateEnabled(true);
        } else {
          setTemplateEnabled(false);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [location.pathname, API_BASE_URL, layout]);

  // Get aspect ratio class based on imageAspect prop
  const getAspectClass = () => {
    switch (imageAspect) {
      case 'square':
        return 'aspect-square';
      case 'portrait':
        return 'aspect-[9/16]';
      case 'landscape':
      default:
        return 'aspect-video';
    }
  };

  // Get grid columns based on image aspect and position
  // For image_left: [image% text%], For image_right: [text% image%]
  const getGridClass = () => {
    const isImageLeft = layout === 'image_left';
    
    switch (imageAspect) {
      case 'square':
        // 30% image, 70% text
        return isImageLeft ? 'lg:grid-cols-[30%_70%]' : 'lg:grid-cols-[70%_30%]';
      case 'portrait':
        // 25% image, 75% text
        return isImageLeft ? 'lg:grid-cols-[25%_75%]' : 'lg:grid-cols-[75%_25%]';
      case 'landscape':
      default:
        // 40% image, 60% text
        return isImageLeft ? 'lg:grid-cols-[40%_60%]' : 'lg:grid-cols-[60%_40%]';
    }
  };

  // If no heroContent provided, just render children
  if (!heroContent) {
    return <>{children}</>;
  }

  // While loading, show hero content centered (default layout)
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <section className="bg-white border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
            <div className="[&>div]:text-center [&>div]:lg:text-center">
              {heroContent}
            </div>
          </div>
        </section>
        {children}
      </div>
    );
  }

  // Template is DISABLED - show hero content centered (no image)
  if (!templateEnabled) {
    return (
      <div className="min-h-screen bg-gray-50">
        <section className="bg-white border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
            <div className="[&>div]:text-center [&>div]:lg:text-center">
              {heroContent}
            </div>
          </div>
        </section>
        {children}
      </div>
    );
  }

  // Template IS enabled - render the layout with image
  const isImageLeft = layout === 'image_left';
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section - Modern Elegant Design with Image */}
      <section className="bg-gradient-to-br from-white via-gray-50/30 to-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          <div className={`grid grid-cols-1 ${getGridClass()} gap-2.5 items-center`}>
            {/* Content */}
            <div className={`${isImageLeft ? 'order-2 lg:order-2 lg:pl-6' : 'order-2 lg:order-1 lg:pr-6'} text-center lg:text-left space-y-3`}>
              <div className="[&>div]:lg:text-left [&_h1]:text-xl [&_h1]:md:text-2xl [&_h1]:font-semibold [&_h1]:text-gray-900 [&_h1]:leading-tight [&_p]:text-gray-600 [&_p]:text-sm [&_p]:leading-relaxed [&_span]:text-xs">
                {heroContent}
              </div>
            </div>
            
            {/* Image - Dynamic positioning based on layout */}
            <div className={`${isImageLeft ? 'order-1 lg:order-1' : 'order-1 lg:order-2'}`}>
              <div className="relative w-full">
                {/* Decorative background element */}
                <div 
                  className={`absolute -top-2 ${isImageLeft ? '-left-2' : '-right-2'} w-full h-full rounded-xl opacity-15`}
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                ></div>
                {/* Image container - Dynamic aspect ratio */}
                <div className={`relative rounded-xl overflow-hidden shadow-lg ring-1 ring-gray-200/50 ${getAspectClass()}`}>
                  <img
                    src={customImage || placeholderImage}
                    alt="Hero"
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                  {/* Subtle gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-black/10 via-transparent to-white/5"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Page Content */}
      {children}
    </div>
  );
};

export default InnerPageLayout;
